import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, FilterPickerGroup, Page, PageContentHeader, Spinner, TableList } from 'seagull';
import { routes } from "../../../constants";
import { useIoContext, useStore, useStoreActions } from "../../../contexts";
import { useSortFromUrl } from "../../../hooks/useSortFromUrl";
import { selectors } from "../../../store/state-selectors";
import { formatLongMonthUSDate } from "../../../utils";
import { VisibleColumnsPicker } from "../../../components/VisibleColumnsPicker";
import TableFilters from "../components/TableFilters/TableFilters";
import { ActiveServiceRow } from "./ActiveServiceRow";
import { activeServicesReportColumnDefs, activeServicesReportHeaderColumnDefs, activeServicesReportHeaderColumnDefsForExport, filterLabels, getDefaultSort, makeCsv, makeFiltersConfig } from "./ActiveServices.utils";
import { ActiveServicesReportOptionalColumns, FilterType } from "./types";
import { useOnMount } from "../../../hooks/useOnMount";
import { compact } from 'lodash-es';
import styles from "./ActiveServices.module.scss";
import RestfulWrapper from "../../../components/RestfulWrapper/RestfulWrapper";
import { useSetDocumentTitle } from "../../../hooks/useSetDocumentTitle";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const testIdPrefix = 'active-services-report';
const title = 'Active Services Report';
export const ActiveServicesReport = () => {
  const history = useHistory();
  const [activeServicesReportData, setActiveServicesReportData] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const {
    downloadCsv
  } = useIoContext();
  const [visibleColumns, setVisibleColumns] = useState({
    [ActiveServicesReportOptionalColumns.CustomerName]: true,
    [ActiveServicesReportOptionalColumns.ServiceName]: true,
    [ActiveServicesReportOptionalColumns.Trigger]: false,
    [ActiveServicesReportOptionalColumns.Pricing]: false,
    [ActiveServicesReportOptionalColumns.Occurrence]: true,
    [ActiveServicesReportOptionalColumns.Rate]: true,
    [ActiveServicesReportOptionalColumns.LastBilled]: false,
    [ActiveServicesReportOptionalColumns.Assignee]: false,
    [ActiveServicesReportOptionalColumns.AutomaticServiceState]: false,
    [ActiveServicesReportOptionalColumns.Status]: true
  });
  const headerItems = useMemo(() => activeServicesReportHeaderColumnDefs(visibleColumns), [visibleColumns]);
  const sortIds = compact(headerItems.map(item => item.sortId));
  const defaultSort = useMemo(() => getDefaultSort(sortIds), [sortIds]);
  const {
    sort,
    changeSort
  } = useSortFromUrl({
    defaultSort,
    sortIds
  });
  const {
    api
  } = useStoreActions();
  const businessId = useStore(selectors.user.businessId);
  const businessName = useStore(selectors.user.businessName);
  const handleFetchFilterConfig = async () => {
    const [serviceNames, customerNames, agreementNames, assignees] = await Promise.all([api.reports.activeServicesReportServices(), api.reports.activeServicesReportCustomers(), api.reports.activeServicesReportAgreements(), api.reports.activeServicesReportAssignees()]);
    const filterOptions = {
      [FilterType.ServiceName]: serviceNames,
      [FilterType.CustomerName]: customerNames,
      [FilterType.AgreementName]: agreementNames,
      [FilterType.Assignee]: assignees
    };
    return makeFiltersConfig(filterOptions);
  };
  const updateVisibleColumns = columns => {
    setVisibleColumns(columns);
    if (sort) {
      const currentSortId = sortIds[sort.colIndex];
      const visibleKeys = Object.keys(columns).filter(key => !!columns[key]);
      const visibleSortIds = headerItems.filter(item => !item.optionalColumn || visibleKeys.includes(item.optionalColumn)).map(item => item.sortId);
      if (!visibleSortIds.includes(currentSortId)) {
        changeSort(getDefaultSort(visibleSortIds));
      }
    }
  };
  const fetchActiveServicesReport = (selectedFilters, sort) => {
    var _selectedFilters$Filt, _selectedFilters$Filt2;
    if (businessId == null) return;
    setIsLoading(true);
    api.reports.activeServicesReport({
      businessId,
      customers: selectedFilters[FilterType.CustomerName] || [],
      services: selectedFilters[FilterType.ServiceName] || [],
      agreements: selectedFilters[FilterType.AgreementName] || [],
      assignees: selectedFilters[FilterType.Assignee] || [],
      params: {
        sort: sort ? [sortIds[sort.colIndex], sort.sort] : undefined,
        occurrence: selectedFilters[FilterType.Occurrence],
        trigger: selectedFilters[FilterType.Trigger],
        pricing: selectedFilters[FilterType.Pricing],
        automaticServiceState: selectedFilters[FilterType.AutomaticServiceState],
        status: selectedFilters[FilterType.Status],
        lastBilledSince: (_selectedFilters$Filt = selectedFilters[FilterType.LastBilled]) === null || _selectedFilters$Filt === void 0 ? void 0 : _selectedFilters$Filt.from,
        lastBilledUntil: (_selectedFilters$Filt2 = selectedFilters[FilterType.LastBilled]) === null || _selectedFilters$Filt2 === void 0 ? void 0 : _selectedFilters$Filt2.to
      }
    }).then(data => setActiveServicesReportData(data)).finally(() => setIsLoading(false));
  };
  useSetDocumentTitle(title);
  useOnMount(() => {
    fetchActiveServicesReport(selectedFilters, sort);
  });
  const onExport = () => downloadCsv(makeCsv(activeServicesReportHeaderColumnDefsForExport(visibleColumns).map(_ref => {
    let {
      value
    } = _ref;
    return value;
  }), visibleColumns, activeServicesReportData), "".concat(businessName, "_active_services_report_").concat(formatLongMonthUSDate(new Date()), ".csv"));
  const onSortChange = sort => {
    changeSort(sort);
    fetchActiveServicesReport(selectedFilters, sort);
  };
  return /*#__PURE__*/_jsx(Page, {
    "data-testid": testIdPrefix,
    header: title,
    variant: "containedHeight",
    onClickBack: () => history.push(routes.reports),
    actions: /*#__PURE__*/_jsx(Button, {
      skin: "primary",
      "data-testid": "".concat(testIdPrefix, "-export-btn"),
      onClick: onExport,
      children: "Export"
    }),
    children: /*#__PURE__*/_jsxs(TableList.Provider, {
      columnDefs: activeServicesReportColumnDefs(visibleColumns),
      numRows: activeServicesReportData.length,
      sort: sort,
      onSortChange: onSortChange,
      children: [/*#__PURE__*/_jsx(PageContentHeader, {
        children: /*#__PURE__*/_jsx("div", {
          className: styles.pageContent,
          children: /*#__PURE__*/_jsxs(FilterPickerGroup, {
            flexWrap: true,
            children: [/*#__PURE__*/_jsx(VisibleColumnsPicker, {
              testId: testIdPrefix,
              visibleColumns: visibleColumns,
              onApply: updateVisibleColumns,
              fixedColumns: {
                [ActiveServicesReportOptionalColumns.ServiceName]: true,
                [ActiveServicesReportOptionalColumns.CustomerName]: true
              }
            }), /*#__PURE__*/_jsx(RestfulWrapper, {
              fetch: handleFetchFilterConfig,
              renderLoader: () => /*#__PURE__*/_jsx(Spinner, {
                size: 24
              }),
              renderChild: _ref2 => {
                let {
                  data: filterConfig
                } = _ref2;
                return /*#__PURE__*/_jsx(TableFilters, {
                  testIdPrefix: testIdPrefix,
                  filterLabels: filterLabels,
                  filterConfig: filterConfig,
                  selectedFilters: selectedFilters,
                  setSelectedFilters: setSelectedFilters,
                  onRefresh: fetchActiveServicesReport
                });
              }
            })]
          })
        })
      }), /*#__PURE__*/_jsx(TableList.Structure, {
        header: /*#__PURE__*/_jsx(TableList.Header, {
          stickyToTop: true,
          items: headerItems,
          "data-testid": "".concat(testIdPrefix, "-table-header")
        }),
        content: isLoading ? /*#__PURE__*/_jsx(Spinner, {
          centeredInBox: true
        }) : /*#__PURE__*/_jsx(_Fragment, {
          children: activeServicesReportData.map((activeService, index) => /*#__PURE__*/_jsx(ActiveServiceRow, {
            index: index,
            data: activeService,
            visibleColumns: visibleColumns
          }, "".concat(activeService.customerName, "-").concat(activeService.serviceName, "-").concat(index)))
        })
      })]
    })
  });
};